import React, { useState, useMemo, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { SwitchTransition, Transition } from 'react-transition-group';

import { H2, Icon, NewButton, Text } from '../../atoms';
import { Modal } from '../../molecules';

import slide1Gif from '../../../assets/images/nux/slide_1.gif';
import slide2Gif from '../../../assets/images/nux/slide_2.gif';
import slide3Gif from '../../../assets/images/nux/slide_3.gif';
import slide4Gif from '../../../assets/images/nux/slide_4.gif';
import slide5Gif from '../../../assets/images/nux/slide_5.gif';
import slide6Gif from '../../../assets/images/nux/slide_6.gif';
import slide7Gif from '../../../assets/images/nux/slide_7.gif';
import slide8Gif from '../../../assets/images/nux/slide_8.gif';

const ModalHeader = styled.div`
  padding: 0;
  position: relative;
  overflow: hidden;
  border-top-right-radius: 24px;
  border-top-left-radius: 24px;

  height: 555px;
  background: var(--lavender-70);

  @media (max-width: 768px) {
    border-top-right-radius: 0;
    border-top-left-radius: 0;
  }
}`;

const SlideShadow = styled.div`
  display: flex;
  z-index: 1;
  position: absolute;
  height: 1px;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: var(--border-muted);
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 32px;
  overflow: hidden;
}`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 32px 32px 32px;
`;

const CloseButton = styled(NewButton)`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  border-radius: 50%;
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;

  transition: opacity 600ms cubic-bezier(0.19, 1, 0.22, 1),
    transform 600ms cubic-bezier(0.19, 1, 0.22, 1);

  ${({ state }) => {
    switch (state) {
      case 'entering':
        return `
          opacity: 0;
          transform: scale(0.9);
        `;
      case 'entered':
        return `
          opacity: 1;
          transform: scale(1);
        `;
      case 'exiting':
        return `
          opacity: 0;
          transform: scale(1.1);
        `;
      default:
        return `
          opacity: 0;
          transform: scale(0.9);
        `;
    }
  }}
`;

const SlideCaptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;

  transition: opacity 600ms linear, transform 600ms cubic-bezier(0.19, 1, 0.22, 1);

  ${({ state }) => {
    switch (state) {
      case 'entering':
        return `
          opacity: 0;
          transform: translateX(100%);
        `;
      case 'entered':
        return `
          opacity: 1;
          transform: scale(1);
        `;
      case 'exiting':
        return `
          opacity: 0;
          transform: translateX(-100%);
        `;
      default:
        return `
          opacity: 0;
          transform: translateX(-100%);
        `;
    }
  }}
`;

const DotsContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 6px;
`;

const Dot = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  background: ${({ $active }) => ($active ? 'var(--lavender-100)' : 'var(--purple-20)')};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  column-gap: 12px;
`;

const CaptionText = styled(Text)`
  height: 60px;
`;

const HelpCircleIcon = styled(Icon)`
  vertical-align: text-bottom;
`;

const NuxModal = ({ onComplete, isReadOnly = false }) => {
  const [index, setIndex] = useState(0);

  const slideGifs = useMemo(
    () => [
      <SlideImage src={slide1Gif} />,
      <SlideImage src={slide2Gif} />,
      <SlideImage src={slide3Gif} />,
      <SlideImage src={slide4Gif} />,
      <SlideImage src={slide5Gif} />,
      <SlideImage src={slide6Gif} />,
      <SlideImage src={slide7Gif} />,
      <SlideImage src={slide8Gif} />,
      <SlideImage src={slide1Gif} />
    ],
    []
  );

  const slideCaptions = useMemo(
    () => [
      <>
        <H2>Ask Questions, Get Insights</H2>
        <CaptionText>
          Start with a broad question like &ldquo;How many users joined in Q2 2024?&rdquo;.
          <br />
          Each question opens a new <strong>Chat</strong> where you can explore further.
          <br />
          Your follow-up questions automatically build on your prior questions.
        </CaptionText>
      </>,
      <>
        <H2>Explore Your Data Naturally</H2>
        <CaptionText>
          Simply describe what you want to see - Supper understands everyday language and finds the
          right data.
          <br />
          Ask Supper to filter, group, or calculate results your way: &ldquo;Show me active users by
          country&rdquo; or &ldquo;What&apos;s the average session time by platform?&rdquo;.
        </CaptionText>
      </>,
      <>
        <H2>Table, Source, Chart Tabs</H2>
        <CaptionText>
          Every answer includes three tabs: <strong>Table</strong> shows your results and an
          explanation, <strong>Source</strong> reveals the underlying SQL, and{' '}
          <strong>Chart</strong> lets you create a data visualization using natural language.
        </CaptionText>
      </>,
      <>
        <H2>Not Quite Right?</H2>
        <CaptionText>
          Click <strong>Retry</strong> to run your question again.
          <br />
          No luck? We&apos;re here to help! Contact us and we&apos;ll help you get the answer
          you&apos;re looking for.
        </CaptionText>
      </>,
      <>
        <H2>Export Results</H2>
        <CaptionText>
          Want to export the results elsewhere? Copy or download data and charts.
        </CaptionText>
      </>,
      <>
        <H2>Data Dictionary</H2>
        <CaptionText>
          Click <strong>View Data Dictionary</strong> to see all synced data across sources.
          <br />
          Add column(s) into the prompt directly.
        </CaptionText>
      </>,
      <>
        <H2>Save Your Insights</H2>
        <CaptionText>
          Turn useful answers or visualizations into Assets by clicking Save.
          <br />
          Give your <strong>Asset</strong> a title and save it to any Space for easy access later.
        </CaptionText>
      </>,
      <>
        <H2>Organize with Spaces</H2>
        <CaptionText>
          Group your Assets into Spaces for easy navigation and sharing.
          <br />
          Edit Space titles and share them with your team.
        </CaptionText>
      </>,
      <>
        <H2>Ready to dive in?</H2>
        <CaptionText>
          Ask your first question and start exploring your data.
          <br />
          Find this guide anytime in the navigation bar under
          <HelpCircleIcon name='help-circle' color='var(--content-subtitle)' displaySize={20} />
        </CaptionText>
      </>
    ],
    []
  );

  const handlePrev = () => setIndex((prev) => Math.max(0, prev - 1));
  const handleNext = () => setIndex((prev) => Math.min(slideGifs.length - 1, prev + 1));

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      handlePrev();
    } else if (event.key === 'ArrowRight') {
      handleNext();
    }
  };

  // Add event listener when component mounts
  useLayoutEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleOnComplete = (close) => {
    onComplete?.();
    close();
  };

  const renderCompleteButton = (close) => {
    if (index < slideGifs.length - 1) {
      return null;
    }

    if (isReadOnly) {
      return <NewButton onClick={close}>Okay, got it!</NewButton>;
    }

    return (
      <NewButton onClick={() => handleOnComplete(close)}>
        Start asking <Icon name='qa' />
      </NewButton>
    );
  };

  return (
    <Modal size='md' bodyStyle={{ width: 800 }}>
      {(close) => (
        <>
          <ModalHeader>
            <CloseButton onClick={close} tertiary>
              <Icon name='close' />
            </CloseButton>
            <SwitchTransition>
              <Transition key={index} timeout={300} mountOnEnter unmountOnExit>
                {(state) => React.cloneElement(slideGifs[index], { state })}
              </Transition>
            </SwitchTransition>
            <SlideShadow />
          </ModalHeader>

          <ModalBody>
            <SwitchTransition>
              <Transition key={index} timeout={300} mountOnEnter unmountOnExit>
                {(state) => (
                  <SlideCaptionContainer state={state}>
                    {slideCaptions[index]}
                  </SlideCaptionContainer>
                )}
              </Transition>
            </SwitchTransition>
          </ModalBody>
          <ModalFooter>
            <DotsContainer>
              {slideCaptions.map((_, i) => (
                <Dot key={i} $active={i === index} onClick={() => setIndex(i)} />
              ))}
            </DotsContainer>

            <Controls>
              {index !== 0 && (
                <NewButton onClick={() => setIndex((prev) => prev - 1)}>
                  <Icon name='arrow-left' /> Previous
                </NewButton>
              )}
              {index < slideGifs.length - 1 ? (
                <NewButton onClick={() => setIndex((prev) => prev + 1)}>
                  Next <Icon name='arrow-right' />
                </NewButton>
              ) : (
                renderCompleteButton(close)
              )}
            </Controls>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

NuxModal.propTypes = {
  onComplete: PropTypes.func,
  isReadOnly: PropTypes.bool
};

export { NuxModal };
